import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { ContentItem, FEED_TYPE_FACEBOOK, FEED_TYPE_LINKEDIN } from 'interfaces'
import { contentTypeFeatureMap, feedTypeFeatureMap, getContentAgeFeature } from 'services/product'
import { userProductSelector } from 'services/users/selectors'
import StoreState from 'store/state'

const FREE_FEED_TYPES = [FEED_TYPE_FACEBOOK, FEED_TYPE_LINKEDIN]

type UserProduct = ReturnType<typeof userProductSelector>

// EXPL: Mirrors `checkFeatureAvailability(feature, true)` as a pure function. A feature the product
// map has no key for is not gated at all - the thunk reports such a feature as unavailable, which
// used to mark the card blocked under an undefined feature name.
function isFeatureAvailable(product: UserProduct, feature?: string | null) {
  if (!feature || !product) {
    return true
  }

  return Boolean(product.features[feature])
}

/**
 * The feature gating this card's content, or null when nothing gates it.
 *
 * EXPL: Reads the product through a selector rather than dispatching `checkFeatureAvailability`
 * during render. The thunk calls `getState()`, so the card never subscribed to the product and
 * kept showing the upgrade overlay after the user upgraded, until an unrelated re-render.
 */
export function useCardBlockedFeature(content: ContentItem, skip?: boolean): string | null {
  const product = useSelector((state: StoreState) => userProductSelector(state))

  return useMemo(() => {
    if (skip) {
      return null
    }

    // EXPL: Each gate is checked in turn - an earlier gate that does not apply must not stop the
    // later ones from being evaluated.
    const feedFeature = content.isStock || FREE_FEED_TYPES.includes(content.feed.type)
      ? null
      : feedTypeFeatureMap[content.feed.type]
    if (!isFeatureAvailable(product, feedFeature)) {
      return feedFeature
    }

    const typeFeature = contentTypeFeatureMap[content.type]
    if (!isFeatureAvailable(product, typeFeature)) {
      return typeFeature
    }

    const ageFeature = content.isStock ? null : getContentAgeFeature(content.createdAt)
    if (!isFeatureAvailable(product, ageFeature)) {
      return ageFeature
    }

    return null
  }, [product, content, skip])
}

export default useCardBlockedFeature
